/**
 * @file FormModal.tsx
 * @description Reusable form modal component with loading state and action buttons
 * @module components/shared
 */

import type { ReactNode } from 'react';
import { Modal, LoadingOverlay, Stack, Group, Button } from '@mantine/core';

interface FormModalProps {
  /** Whether modal is open */
  opened: boolean;
  /** Close handler */
  onClose: () => void;
  /** Modal title */
  title: string;
  /** Whether form is submitting */
  isLoading?: boolean;
  /** Submit handler */
  onSubmit: () => void;
  /** Submit button text */
  submitLabel?: string;
  /** Submit button color */
  submitColor?: string;
  /** Modal size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Form content */
  children: ReactNode;
}

/**
 * Reusable modal with form layout, loading overlay, and action buttons
 */
export function FormModal({
  opened,
  onClose,
  title,
  isLoading = false,
  onSubmit,
  submitLabel = 'Submit',
  submitColor,
  size = 'md',
  children,
}: FormModalProps) {
  return (
    <Modal opened={opened} onClose={onClose} title={title} size={size}>
      <LoadingOverlay visible={isLoading} />
      <Stack gap="md">
        {children}
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button color={submitColor} onClick={onSubmit}>
            {submitLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
